import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const prophetSwing: WikiTopic = {
  slug: "prophet-swing",
  title: "Prophet swing",
  summary: "Weekly envelope support with the daily Stochastic RSI crossing up, and its exit.",
  group: "strategies",
  horizon: "swing",
  strategyKey: "prophet_swing",
  sections: [
    {
      heading: "When it runs",
      code: "prophet_swing_entry / prophet_swing_exit",
      blocks: [
        p(
          <>
            Two legs: a weekly context filter and a daily trigger. Both are defined on a completed
            daily candle, so both are read once a session, on bucket{" "}
            <Datum>{QUOTED.PROPHET_SWING_SESSION_BUCKET}</Datum> of the following session. That
            bucket closes at <Datum>10:15</Datum> and it is the first one that can act on a candle
            which completed the evening before.
          </>,
        ),
        p(
          <>
            The <Datum>09:15-10:15</Datum> close is the price on the row, because it is the first
            price a reader could act at. The daily close the rules were read on rides along in the
            payload as <Datum>triggerClose</Datum>. The two are separated by one overnight gap, and
            carrying both is what makes this engine's slippage against the source's fill measurable
            rather than assumed.
          </>,
        ),
        note(
          <>
            The runner's minimum of <Datum>{QUOTED.PROPHET_SWING_MIN_BARS}</Datum> hourly buckets
            never binds. The weekly filter needs <Datum>{QUOTED.MIN_COMPLETED_WEEKS}</Datum>{" "}
            completed weeks, so any name that clears the daily legs cleared this months ago.
          </>,
        ),
      ],
    },
    {
      heading: "The weekly filter",
      blocks: [
        p(
          <>
            A trend test plus an envelope-support touch, and the two are load-bearing for each
            other. The trend test is price structure, not a crossover: this week's close has to sit
            above the close <Datum>{QUOTED.TREND_LOOKBACK_WEEKS}</Datum> weeks back. The source this
            came from rejects the crossover reading outright and takes a crossed-below setup.
          </>,
        ),
        p(
          <>
            The envelope is the weekly SMA <Datum>{QUOTED.FAST_SMA_PERIOD}</Datum> and SMA{" "}
            <Datum>{QUOTED.SLOW_SMA_PERIOD}</Datum>. A week arms the filter when its low lands
            between the two lines in either order. That orientation-free reading is deliberate,
            because a qualifying week can have the fast line under the slow one and the literal
            reading has no solution there. The trend test is what keeps a downtrend out.
          </>,
        ),
        p(
          <>
            The scan runs oldest first over the last <Datum>{QUOTED.LEG1_LOOKBACK_WEEKS}</Datum>{" "}
            completed weeks plus the week in progress. An older arming week imposes a weaker
            ordering constraint on the trigger, so taking the oldest qualifying touch never drops a
            setup a newer one would have allowed.
          </>,
        ),
        note(
          <>
            The week in progress contributes its running low and nothing else. A running minimum is
            monotone, so reading it is not lookahead; a running close is. Its colour and volume come
            back null because a forming week has neither, and colour is recorded rather than gated:
            a green week at envelope support may already be the move, and the two populations have
            to be measurable apart before anyone narrows the filter.
          </>,
        ),
      ],
    },
    {
      heading: "The daily trigger",
      blocks: [
        p(
          <>
            Three events in order. Both Stochastic RSI lines sit under{" "}
            <Datum>{QUOTED.STOCH_OVERSOLD}</Datum> within the last{" "}
            <Datum>{QUOTED.OVERSOLD_LOOKBACK_SESSIONS}</Datum> sessions, %K then crosses up through
            %D no more than <Datum>{QUOTED.CROSS_LOOKBACK_SESSIONS}</Datum> sessions ago, and a
            green candle closing above a recent red open confirms it within{" "}
            <Datum>{QUOTED.GREEN_GRACE_SESSIONS}</Datum> sessions of the cross.
          </>,
        ),
        p(
          <>
            The oversold session must be strictly earlier than the cross. Two independent windows
            would admit a symbol that crossed up and has since fallen back under{" "}
            <Datum>{QUOTED.STOCH_OVERSOLD}</Datum>, which reads a failing setup as a starting one.
          </>,
        ),
        p(
          <>
            An oscillation count rides on the row: how many times %K traversed between{" "}
            <Datum>{QUOTED.STOCH_OVERSOLD}</Datum> and{" "}
            <Datum>{QUOTED.STOCH_OVERBOUGHT_FOR_OSCILLATION}</Datum> over the last{" "}
            <Datum>{QUOTED.OSCILLATION_WINDOW_SESSIONS}</Datum> sessions. A name pinned near the
            middle for months passes the trigger the moment it dips under{" "}
            <Datum>{QUOTED.STOCH_OVERSOLD}</Datum> and crosses, and the source would reject it. The
            count is recorded and gates nothing yet.
          </>,
        ),
        table(
          "Prophet swing levels",
          ["Level", "How it is set"],
          [
            [
              "Stop",
              <Datum key="s">
                {QUOTED.PIVOT_BUFFER_ATR} ATR under the pivot low of the last{" "}
                {QUOTED.PIVOT_LOOKBACK_SESSIONS} sessions
              </Datum>,
            ],
            ["Target", <Datum key="t">The daily Bollinger upper band</Datum>],
            ["ATR period", <Datum key="a">{QUOTED.DAILY_ATR_PERIOD} sessions, daily</Datum>],
          ],
        ),
      ],
    },
    {
      heading: "One walk, one row",
      blocks: [
        p(
          <>
            A single forward pass over the daily series drives both paths. It carries an open-flat
            flag that never leaves the call, so nothing is remembered between sessions and nothing
            can repaint.
          </>,
        ),
        p(
          <>
            The walk dedups entries as well as exits. The trigger stays armed for two or three
            consecutive sessions, so testing the entry on its own would write a row on each of them
            with a different price, stop, target, and magnitude, and the unique index collapses none
            of those because they differ by <Datum>session_date</Datum>. An entry fires only when
            the walk's open position started on the newest completed session, which is the same
            transition-edge test the exit applies.
          </>,
        ),
        p(
          <>
            The exit wins when both are available in one call. A position can close on the same
            session a new one arms, an open position's exit is the more urgent of the two, and a
            missed exit is not re-offered until tomorrow.
          </>,
        ),
        p(
          <>
            Five conditions close a position, and any one of them is enough: the close falls below
            the entry's stop, both Stochastic RSI lines sit above{" "}
            <Datum>{QUOTED.STOCH_OVERBOUGHT}</Datum>, the session's low clears the upper Bollinger
            band, the bar prints a topping tail, or it tweezers a top against the session before it.
            The payload names which one fired, so a reason that never pays can be measured out
            rather than argued about.
          </>,
        ),
        note(
          <>
            The two shape conditions skip a one-hour ceremonial session. A wick-to-body ratio on a
            Muhurat bar describes the ceremony, not the name.
          </>,
        ),
        ul([
          <>An exit fires on the first session its condition is true, never on the ones after.</>,
          <>
            Its side is <Datum>short</Datum>, because the side names the fill and closing a long is
            a sell. Drawn as a long it would read as a second entry.
          </>,
          <>
            Its stop and target are both <Datum>null</Datum>. There is nothing to stop out of once
            the position is closed; the stop the trade ran under is in the payload, fixed as of the
            entry session and never recomputed.
          </>,
        ]),
      ],
    },
    {
      heading: "What the row carries",
      blocks: [
        p(
          <>
            Entry magnitude is reward in daily ATR, not reward over risk. Reward over risk climbs as
            the stop tightens, and severity reads a bigger magnitude as more urgent, so the second
            reading would make the tightest-stopped worst entries the loudest. Reward over risk
            survives on the payload as <Datum>rewardRisk</Datum>, where it is readable and steers
            nothing.
          </>,
        ),
        p(
          <>
            Exit magnitude is the absolute R multiple. A <Datum>-1R</Datum> exit is as worth knowing
            as a <Datum>+3R</Datum> one, and the sign is in the payload. Entry and exit therefore
            carry different units on purpose: severity looks its table up per signal type, so the
            two never share a scale.
          </>,
        ),
        p(
          <>
            It does not vote in confluence. A swing entry driven by a weekly setup is not
            independent intraday evidence, and confluence rows deliver at a louder severity than
            this ships at, so voting would route it to Telegram by accident.
          </>,
        ),
        p(
          <>
            The rows carry family <Datum>intraday</Datum> while the strategy holds for days, because
            family names the job that emitted the row and horizon names how long the strategy means
            to hold. See the family and horizon topic for why those two answers differ here.
          </>,
        ),
        note(
          <>
            Exits do not replay. A missed evaluation at bucket{" "}
            <Datum>{QUOTED.PROPHET_SWING_SESSION_BUCKET}</Datum> drops that session's exits for
            good, because the exit fires on the transition edge only and the next session no longer
            sits on it.
          </>,
        ),
      ],
    },
  ],
};
